import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ChevronDown, ChevronUp, Trash2, MessageCircle, ClipboardList } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { formatGs } from "../../data/store";

const STATUSES = [
  { key: "nuevo", label: "Nuevo", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  { key: "contactado", label: "Contactado", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  { key: "entregado", label: "Entregado", cls: "bg-green-50 text-green-700 border-green-200" },
  { key: "cancelado", label: "Cancelado", cls: "bg-gray-100 text-gray-500 border-gray-200" },
];
const statusMeta = (k) => STATUSES.find((s) => s.key === k) || STATUSES[0];
const fmtDate = (iso) => new Date(iso).toLocaleString("es-PY", { dateStyle: "short", timeStyle: "short" });
const phoneDigits = (p) => (p || "").replace(/\D/g, "");

export const OrdersManager = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setOrders(await api.adminOrders(filter)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeStatus = async (o, status) => {
    try {
      const upd = await api.updateOrderStatus(o.id, status);
      setOrders((prev) => prev.map((x) => (x.id === o.id ? upd : x)));
      toast.success(`Pedido #${o.number} → ${statusMeta(status).label}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (o) => {
    if (!window.confirm(`¿Eliminar el pedido #${o.number}?`)) return;
    try { await api.deleteOrder(o.id); toast.success("Pedido eliminado"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s.key]: orders.filter((o) => o.status === s.key).length }), {});

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Pedidos recibidos</h1>
          <p className="text-gray-500 text-sm">{orders.length} pedidos{filter ? ` (${statusMeta(filter).label.toLowerCase()})` : ""} · historial de pedidos enviados por WhatsApp</p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="orders-filters">
          <button data-testid="orders-filter-todos" onClick={() => setFilter("")}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border ${!filter ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 border-gray-300"}`}>Todos</button>
          {STATUSES.map((s) => (
            <button key={s.key} data-testid={`orders-filter-${s.key}`} onClick={() => setFilter(s.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border ${filter === s.key ? "bg-gray-900 text-white border-gray-900" : `${s.cls}`}`}>
              {s.label}{!filter && counts[s.key] ? ` · ${counts[s.key]}` : ""}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-24 text-gray-400"><Loader2 className="w-8 h-8 animate-spin" /></div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-20 text-center text-gray-400" data-testid="orders-empty">
          <ClipboardList className="w-10 h-10 mx-auto mb-3" />
          <p className="font-semibold">Todavía no hay pedidos{filter ? " con ese estado" : ""}.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm" data-testid="admin-orders-table">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">#</th>
                <th className="text-left px-4 py-3">Fecha</th>
                <th className="text-left px-4 py-3">Cliente</th>
                <th className="text-right px-4 py-3 hidden sm:table-cell">Ítems</th>
                <th className="text-right px-4 py-3">Total</th>
                <th className="text-left px-4 py-3">Estado</th>
                <th className="text-right px-4 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {orders.map((o) => {
                const isOpen = openId === o.id;
                const qty = o.items.reduce((s, i) => s + i.qty, 0);
                return [
                  <tr key={o.id} data-testid={`order-row-${o.number}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono-code font-bold text-[#0A3977]">#{o.number}</td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{fmtDate(o.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900">{o.customer_name}</div>
                      {o.phone && <div className="text-xs text-gray-400">{o.phone}</div>}
                    </td>
                    <td className="px-4 py-3 text-right hidden sm:table-cell">{qty}</td>
                    <td className="px-4 py-3 text-right font-bold text-[#E02424] whitespace-nowrap">{formatGs(o.total)}</td>
                    <td className="px-4 py-3">
                      <select
                        data-testid={`order-status-${o.number}`} value={o.status} onChange={(e) => changeStatus(o, e.target.value)}
                        className={`px-2 py-1 rounded-full text-xs font-bold border outline-none cursor-pointer ${statusMeta(o.status).cls}`}
                      >
                        {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {phoneDigits(o.phone) && (
                          <a data-testid={`order-whatsapp-${o.number}`} target="_blank" rel="noreferrer"
                            href={`https://wa.me/${phoneDigits(o.phone)}?text=${encodeURIComponent(`Hola ${o.customer_name}, te escribimos de Promax Tintas por tu pedido #${o.number}.`)}`}
                            className="p-2 rounded-lg hover:bg-green-50 text-[#25D366]" title="Escribir al cliente"><MessageCircle className="w-4 h-4" /></a>
                        )}
                        <button data-testid={`order-toggle-${o.number}`} onClick={() => setOpenId(isOpen ? null : o.id)} className="p-2 rounded-lg hover:bg-blue-50 text-[#0067B1]">
                          {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        <button data-testid={`order-delete-${o.number}`} onClick={() => remove(o)} className="p-2 rounded-lg hover:bg-red-50 text-[#E02424]"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>,
                  isOpen && (
                    <tr key={`${o.id}-detail`} data-testid={`order-detail-${o.number}`} className="bg-[#FAFAFA]">
                      <td colSpan={7} className="px-6 py-4">
                        <div className="grid md:grid-cols-[1fr_280px] gap-6">
                          <table className="w-full text-xs">
                            <thead className="text-gray-400 uppercase tracking-wide">
                              <tr><th className="text-left pb-2">Código</th><th className="text-left pb-2">Producto</th><th className="text-right pb-2">Cant.</th><th className="text-right pb-2">Unit.</th><th className="text-right pb-2">Subtotal</th></tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {o.items.map((i, idx) => (
                                <tr key={idx}>
                                  <td className="py-1.5 font-mono-code text-gray-500">{i.code}</td>
                                  <td className="py-1.5 font-semibold text-gray-900">{i.name} <span className="text-gray-400 font-normal">· {i.unit}</span></td>
                                  <td className="py-1.5 text-right">{i.qty}</td>
                                  <td className="py-1.5 text-right">{formatGs(i.price)}</td>
                                  <td className="py-1.5 text-right font-bold">{formatGs(i.price * i.qty)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <div className="text-sm">
                            <div className="text-xs font-bold tracking-widest uppercase text-gray-400">Observaciones</div>
                            <p className="mt-1 text-gray-700 whitespace-pre-wrap">{o.notes || <span className="text-gray-400">Sin observaciones</span>}</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
